import styled from 'foundations-theming/utils';
import { generatePath, useLocation, useParams } from 'react-router-dom';
import { useEffect } from 'react';
import I18n from 'I18n';
import ACMissingContentError from 'academy-common-lib/components/error/ACMissingContentError';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { getFullUrl } from 'hubspot-url-utils';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { Button } from 'foundations-components/transitional/components/Button';
import { EditorPage } from 'foundations-components/transitional/components/EditorPage';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { ToolBar } from 'foundations-components/transitional/components/ToolBar';
import { ToolBarGroup } from 'foundations-components/transitional/components/ToolBarGroup';
import { useGetToolRecommendations } from '../queries/softwareDiscoveryHooks';
import { useRequestLearnWhileDoing } from '../hooks';
import LearnByDoingDraggableContainer from './LearnByDoing/DraggableContainer/LearnByDoingDraggableContainer';
import { useHomepageUrl } from '../../navigation/navigationQueries';
import UsageTracker from '../../../utils/UsageTracker';
import { NavMarker } from 'react-rhumb';
import { useShort } from '../../shortContent/shortContentQueries';
import useNavigate from '../../../hooks/useNavigate';
import { SHORT_CONTENT_URL_FRAGMENT } from '../../../constants/Navigation';

// @bend-expect trellis-tokenized
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const FullscreenIframe = styled.iframe.withConfig({
  displayName: "SoftwareDiscoveryIframe__FullscreenIframe"
})(["width:100%;height:100%;border:0;overflow:scroll;position:absolute;"]);
const StyledText = styled.span.withConfig({
  displayName: "SoftwareDiscoveryIframe__StyledText"
})(["color:", ";h5{color:", ";}"], ({
  theme
}) => theme.color['text-core-default'], ({
  theme
}) => theme.color['text-core-default']);
const SoftwareDiscoveryIframe = ({
  toolSlugProp = '',
  useCases
}) => {
  const {
    homepage
  } = useHomepageUrl();
  const homePageUrl = homepage ? `${getFullUrl('app')}${homepage}` : undefined;
  const location = useLocation();
  const navigate = useNavigate();
  const {
    toolSlug = ''
  } = useParams();
  const {
    search
  } = location;
  const params = new URLSearchParams(search);
  const experimentUseCases = params.get('useCases') || useCases || '';
  const otgId = params.get('otgId');
  const isLearnWhileDoing = !!JSON.parse(params.get('isLearnWhileDoing') || 'false');
  const {
    handleEnableLearnWhileDoing,
    handleDisableLearnWhileDoing,
    isLearnWhileDoingEnabled
  } = useRequestLearnWhileDoing();
  useEffect(() => {
    if (isLearnWhileDoing) {
      handleEnableLearnWhileDoing();
    }
  }, [isLearnWhileDoing, handleEnableLearnWhileDoing]);
  const {
    short
  } = useShort(Number(otgId), false);
  const {
    hubId: portalId,
    contentHublet: hublet
  } = short || {};
  const toolRecommendations = useGetToolRecommendations([experimentUseCases]);
  const resourceIndex = toolRecommendations && toolRecommendations.toolSlug.indexOf(toolSlug || toolSlugProp);
  const hasToolRecommendations = !!(toolRecommendations && resourceIndex !== undefined && resourceIndex > -1);
  const toolUrl = hasToolRecommendations && toolRecommendations.toolCardLinks[resourceIndex] || homePageUrl;
  const toolLabelTitle = hasToolRecommendations && toolRecommendations.toolLabelTitles[resourceIndex];
  const toolLabel = toolLabelTitle || (homePageUrl ? 'learningCenter.toolRecommendations.learnByDoingEditorHeader' : 'learningCenter.errors.deprecated.heading');
  const toolLoading = toolRecommendations && toolRecommendations.toolLoading;
  useEffect(() => {
    const hideZorse = document.createElement('style');
    hideZorse.innerHTML = `#isc-zorse-widget, #chat-widget {
        display:none; }`;
    document.getElementsByTagName('head')[0].appendChild(hideZorse);
    return function cleanup() {
      handleDisableLearnWhileDoing();
      hideZorse.remove();
    };
  }, [handleDisableLearnWhileDoing]);
  const closeIframe = () => {
    window.close();
  };
  const handleExitIframe = () => {
    if (isLearnWhileDoingEnabled) {
      const otgPath = generatePath(SHORT_CONTENT_URL_FRAGMENT, {
        shortId: otgId
      });
      handleDisableLearnWhileDoing();
      navigate(otgPath);
    } else {
      closeIframe();
    }
    UsageTracker.track('contentInteraction', {
      action: 'learn-by-doing-back-to-academy-clicked',
      shortId: String(otgId)
    });
  };
  return /*#__PURE__*/_jsx(EditorPage, {
    hasScrolling: true,
    headerComponent: /*#__PURE__*/_jsx(View, {
      backgroundColor: "fill-info-subtle",
      children: /*#__PURE__*/_jsxs(ToolBar, {
        paddingBlock: "200",
        paddingInline: "700",
        use: "filter-bar",
        children: [/*#__PURE__*/_jsx(ToolBarGroup, {
          children: /*#__PURE__*/_jsx(StyledText, {
            children: /*#__PURE__*/_jsx(Text, {
              as: "h5",
              children: isLearnWhileDoingEnabled ? /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.toolRecommendations.learnByDoingEditorHeader"
              }) : /*#__PURE__*/_jsx(FormattedJSXMessage, {
                elements: {
                  Message: FormattedMessage
                },
                message: "learningCenter.toolRecommendations.iframeHeader_jsx",
                options: {
                  toolLabel
                }
              })
            })
          })
        }), /*#__PURE__*/_jsx(ToolBarGroup, {
          children: "\xA0"
        }), /*#__PURE__*/_jsx(ToolBarGroup, {
          children: /*#__PURE__*/_jsx(Button, {
            "data-test-id": "back-to-academy-button-cta",
            onClick: handleExitIframe,
            use: "tertiary",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.toolRecommendations.backToAcademy"
            })
          })
        })]
      })
    }),
    inset: "0",
    mainSectionFlush: true,
    position: "absolute",
    use: "dark",
    zIndex: 5,
    children: toolLoading ? /*#__PURE__*/_jsx(LoadingSpinner, {
      grow: true,
      minHeight: 200
    }) : isLearnWhileDoingEnabled && toolUrl ? /*#__PURE__*/_jsx(DndProvider, {
      backend: HTML5Backend,
      children: /*#__PURE__*/_jsx(LearnByDoingDraggableContainer, {
        creatorHublet: hublet,
        creatorPortalId: portalId,
        iFrameId: "software-discovery-iframe",
        otgId: Number(otgId),
        children: /*#__PURE__*/_jsx(NavMarker, {
          name: "SOFTWARE_DISCOVERY_LOADED",
          children: /*#__PURE__*/_jsx(FullscreenIframe, {
            "data-test-id": "software-discovery-iframe",
            id: "software-discovery-iframe",
            src: toolUrl,
            title: I18n.text('learningCenter.toolRecommendations.AcademyLearnByDoing')
          })
        })
      })
    }) : toolLabelTitle && toolUrl ? /*#__PURE__*/_jsx(NavMarker, {
      name: "SOFTWARE_DISCOVERY_LOADED",
      children: /*#__PURE__*/_jsx(FullscreenIframe, {
        "data-test-id": "software-discovery-iframe",
        id: "software-discovery-iframe",
        src: toolUrl,
        title: I18n.text(toolLabelTitle)
      })
    }) : /*#__PURE__*/_jsx(NavMarker, {
      name: "SOFTWARE_DISCOVERY_ERROR",
      children: /*#__PURE__*/_jsx(ACMissingContentError, {})
    })
  });
};
export default SoftwareDiscoveryIframe;